/** @module components/slide-control */
import _ from "underscore";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import events from "web/script/dom/events";
import style from "web/script/dom/style";
import traversal from "web/script/dom/traversal";
import { emptyElement } from "web/script/dom/manipulation";
import templateLoadingSpinner from "templates/loading-spinner.html";
import templateSlideControl from "templates/components/slide-control.html";

var SELECTOR_SLIDE_STRIP = "[data-slide-strip]";
var SELECTOR_SLIDE = "[data-slide]";
var CLASS_SLIDE_STRIP_SNAPPING = "slide-control__slides--snapping";
var CLASS_SOLO_SLIDE = "slide-control--solo-slide";

var KEYS = {
    ArrowUp: 38,
    ArrowRight: 39,
    ArrowDown: 40,
    ArrowLeft: 37,
};

/**
 * This is a component that lets you swipe or scroll through a group of elements.
 * @constructor
 * @alias module:components/swipe-control
 * @extends module:components/components
 */
export default baustein.register(
    "slide-control",
    /** @lends module:components/slide-control.prototype */
    {
        template: templateSlideControl,

        defaultOptions: {
            /**
             * The default set of items.
             */
            items: null,
            /**
             * The selected index to initialise to. This cannot be mutated after the
             * component is created.
             */
            selectedIndex: 0,
            /**
             * Should the user be able to press <- and -> to change the image?
             */
            keyboardControls: true,
            /**
             * Should the image be offset to the side, rather than be centered.
             */
            offsetDisplay: false,
            /**
             * Adjust the width of the image when offset. By default, it is 85% the width of
             * the container.
             */
            offsetFactor: 0.85,
            /**
             * When the user puts their finger down, should scrolling be allowed? By default,
             * scrolling is prevented.
             */
            allowScroll: false,
            /**
             * When the user taps on the image, by default it will move to the next image.
             * If set to true, it will instead emit a click event to be handled by the
             * controller component.
             */
            emitClickEvent: false,
            /**
             * If set to true, the loading indicator will be white, otherwise, black.
             */
            whiteLoadingIndicator: false,
        },

        _items: null,
        _currentIndex: null,
        _blocked: null,
        _inserted: false,
        _cachedImages: null,
        _slidesWidth: 0,

        /**
         * Returns the number of current Items
         * @returns {number}
         */
        getItemCount() {
            return this._items ? this._items.length : 0;
        },

        /**
         * Returns the highest available index (shorthand for getItemCount -1)
         * @returns {number}
         */
        getMaxIndex() {
            return this.getItemCount() - 1;
        },

        isSingleSlide() {
            return this.getItemCount() === 1;
        },

        getCurrentIndex() {
            return this._currentIndex;
        },

        init() {
            this._onResizeDebounced = _.debounce(this._updateItemPositions.bind(this), 50);

            var selectedIndex = this.options.selectedIndex;

            this._items = [];
            this._cachedImages = {};

            if (this.options.items) {
                this.addItem(this.options.items);
                this._setSelectedIndex(this._items[selectedIndex] ? selectedIndex : 0);
            }

            if (this.isSingleSlide()) {
                this.block();
            }
        },

        getInitialRenderContext() {
            return {
                offsetDisplay: this.options.offsetDisplay,
                totalDisplayedSlides: this.options.offsetDisplay ? 4 : 3,
                zoomEnabled: this.options.zoomEnabled,
                className: this.options.class,
            };
        },

        setupEvents(add) {
            add("touchstart", this._onTouchStart);
            // we need to prevent default on swipes on the container
            add("touchmove", this._onContainerTouchMove);

            add("click", SELECTOR_SLIDE, this._onItemClick);

            add("load", ".slide-control__image", this._onImageLoaded);
            add("error", ".slide-control__image", this._onImageFailed);

            add("zoomStateStart", "zoomable-element", this._onZoomStateStart);
            add("zoomStateEnd", "zoomable-element", this._onZoomStateEnd);

            add("click", ".slide-control__slide__container", this._preventDefaultExceptLink);
        },

        _onZoomStateStart() {
            this.emit("zoomStateStart");
            this.block();
        },

        _onZoomStateEnd() {
            this.emit("zoomStateEnd");
            this.unblock();
        },

        /**
         * Adds a single item or a list of items to the currents stack
         * @param item {object} or {array}
         * @returns {instance}
         */
        addItem(item) {
            var items = this._items;

            var oldIndex = this.getMaxIndex();
            if (_.isArray(item)) {
                // when we add a array we want to add all elements at the end of the current stake
                items.push(...item);
            } else if (_.isObject(item)) {
                // if it's a single item just add it to the end
                items.push(item);
            }

            // when created, this may not have any items and _currentIndex will be set to null
            if (_.isNull(this._currentIndex)) {
                this._setSelectedIndex(0);
            }

            // if the index has changed (ie. something definitely added) then fire an event
            if (this.getMaxIndex() !== oldIndex) {
                this._onItemsChanged();
            }
        },

        /**
         * Provides a single item based on the index
         * @param {number} index
         * @returns {object}
         */
        getItem(index) {
            return this._items[index] || {};
        },

        /**
         * Block any navigation in the gallery
         */
        block() {
            this._blocked = true;
        },

        /**
         * Activate the navigation
         */
        unblock() {
            this._blocked = false;
        },

        /**
         * Go to the slide with the index. Will throw an error if invalid index specified.
         * @param slideIndex - the index to go to
         * @param fromIndicator - if this action was triggered by an indicator
         */
        goToSlide(slideIndex, fromIndicator = false) {
            slideIndex = parseInt(slideIndex);
            if (_.isNaN(slideIndex)) {
                throw new Error("Slide index is NaN");
            }

            if (slideIndex < 0 || slideIndex > this.getMaxIndex()) {
                throw new Error("Slide index not in range");
            }

            if (!_.isNull(this._currentIndex)) {
                if (this._getRelativeIndex(-1) === slideIndex) {
                    this._snapToImage("left");
                    return;
                }
                if (this._getRelativeIndex(1) === slideIndex) {
                    this._snapToImage("right");
                    return;
                }
            }

            this._setSelectedIndex(slideIndex, fromIndicator);
            this._renderContent();
        },

        onInsert() {
            this.setGlobalHandler("resize", this._onResizeDebounced);

            if (this.options.keyboardControls) {
                this.setGlobalHandler("keyup", this._onKeyUp);
                this.setGlobalHandler("keydown", this._onKeyDown);
            }

            this.render();
            this.afterInsert();
        },

        /**
         * Set up references to DOM nodes that will need to be accessed fairly regularly.
         */
        afterInsert() {
            this._inserted = true;

            this.el.classList.add("slide-control-container");

            this._renderContent();
            this._updateItemPositions();

            if (this.isSingleSlide()) {
                this.block();
                this._setupSingleSlide();
            } else {
                this.unblock();
                this._removeSingleSlide();
            }
        },

        onRemove() {
            this.releaseGlobalHandler("resize", this._onResizeDebounced);
            this.releaseGlobalHandler("keyup", this._onKeyUp);
            this.releaseGlobalHandler("keydown", this._onKeyDown);
        },

        /**
         * Refreshes and rerenders the slides, which may be required if, for instance, the
         * element is now visible or or moved.
         */
        refresh() {
            this.afterInsert();
        },

        /**
         * Go to the next slide
         */
        next() {
            this._snapToImage("right");
        },

        /**
         * Go to the previous slide
         */
        previous() {
            this._snapToImage("left");
        },

        _setupSingleSlide() {
            this.el.classList.add(CLASS_SOLO_SLIDE);
        },

        _removeSingleSlide() {
            this.el.classList.remove(CLASS_SOLO_SLIDE);
        },

        _onItemsChanged() {
            if (!this._inserted) {
                this.afterInsert();
            } else {
                this._renderContent();
            }

            this.emit("change:items", { items: this._items });
        },

        /**
         * Don't want to prevent default sometimes if we're clicking a link
         * @param event
         * @private
         * @returns boolean if the default behaviour was prevented or not
         */
        _preventDefaultExceptLink(event) {
            // unless we're clicking a link, prevent default
            if (!_.isElement(event.target) || event.target.tagName !== "A") {
                event.preventDefault();
                return true;
            } else {
                return false;
            }
        },

        /**
         * When you press a certain key, go next/previous or even close the overlay.
         * @param event
         * @private
         */
        _onKeyUp(event) {
            var key = event.keyCode;

            switch (key) {
                case KEYS.ArrowLeft:
                    this._snapToImage("left");
                    break;
                case KEYS.ArrowRight:
                    this._snapToImage("right");
                    break;
                case KEYS.ArrowUp:
                case KEYS.ArrowDown:
                    event.preventDefault();
                    break;
            }
        },

        /**
         * Prevents the default for up and down keys
         * @param event
         * @private
         */
        _onKeyDown(event) {
            var key = event.keyCode;

            if (key === KEYS.ArrowUp || key === KEYS.ArrowDown) {
                event.preventDefault();
            }
        },

        /**
         * We can't really automatically solve this via CSS gracefully, so set up the
         * image containers via JS.
         * @private
         */
        _updateItemPositions() {
            if (!this.el.parentElement) {
                return;
            }

            var bounds = style.getPosition(this.el);
            var width = bounds.width;
            var height = bounds.height;

            if (this.options.offsetDisplay && !this.isSingleSlide()) {
                width = width * this.options.offsetFactor;
            }

            this._slidesWidth = width;

            _(this.el.querySelectorAll(SELECTOR_SLIDE)).each(function(element, index) {
                style.setStyles(element, {
                    left: -width + width * index,
                    width: width,
                    "max-width": width,
                    height: height,
                    "max-height": height,
                });
            });

            const strip = this.el.querySelector(SELECTOR_SLIDE_STRIP);
            if (strip) {
                style.setStyle(strip, "width", width * 3);
            }
        },

        /**
         * This is for both mouse down and touch start events. When you put your finger down,
         * set up events for moving the images across.
         * @param event
         * @private
         */
        _onTouchStart(event) {
            // check if the touch event has already been handled somewhere else
            if (event.defaultPrevented) {
                return;
            }

            // we only need to support a single finger, so if there are more, ignore this call
            if (event.targetTouches.length > 1) {
                return;
            }

            // prevent the default action if scrolling isn't allowed
            if (!this.options.allowScroll) {
                this._preventDefaultExceptLink(event);
            }

            if (this._blocked) {
                return;
            }

            this._lastDelta = 0;

            this._touchStart = this._getTapPosition(event);

            this.registerEvent("touchmove", SELECTOR_SLIDE_STRIP, this._onTouchMove);
            this.registerEvent("touchend", SELECTOR_SLIDE_STRIP, this._onTouchEnd);
        },

        /**
         * Move the image side to side while you move your finger or mouse. If it's the last
         * image or the first one, add a bit of drag.
         * @param event
         * @private
         */
        _onTouchMove(event) {
            if (this._blocked) {
                return;
            }

            var tap = this._getTapPosition(event);
            var deltaX = tap.x - this._touchStart.x;
            var deltaY = Math.abs(tap.y - this._touchStart.y);

            // we don't allow scrolling? prevent default actions
            // we do allow scrolling? if the Y move distance is less than X, then prevent scrolling
            if (
                !this.options.allowScroll ||
                (this.options.allowScroll && deltaY < Math.abs(deltaX))
            ) {
                event.preventDefault();
                event.stopPropagation();
            }
            this.swiping = true;

            this._lastDelta = deltaX;

            const strip = this.el.querySelector(SELECTOR_SLIDE_STRIP);
            if (strip) {
                style.setStyle(strip, "transform", `translateX(${deltaX}px)`);
            }
        },

        /**
         * A cross platform way of getting the active position. If a click is passed, it will
         * get the screenX and screenY from the click event. If a touch is passed, it will
         * get the screenX and screenY from the touch events first finger.
         * @param {MouseEvent|TouchEvent} event - the event object to process
         * @returns {{x: Number, y: Number}} the x and y coordinates of the touch/click
         * @private
         */
        _getTapPosition(event) {
            var tap = event.touches ? event.touches[0] : event;
            return {
                x: tap.screenX,
                y: tap.screenY,
            };
        },

        _onContainerTouchMove(event) {
            if (!this.options.allowScroll) {
                event.preventDefault();
            }
        },

        /**
         * When you complete the touch/mouse movement, determine if you're going to the next
         * or previous image, and release any events.
         * @param event
         * @private
         */
        _onTouchEnd(event) {
            if (!this._preventDefaultExceptLink(event)) {
                // allow link touches to pass through
                return;
            }

            event.stopPropagation();

            var screenWidth = this._slidesWidth;
            var threshold = Math.min(screenWidth / 4.5, 100);

            if (!this.swiping) {
                this._unbindTouches();
                this._onItemClick(event);
                return;
            }

            this.swiping = false;

            var goingNext = this._lastDelta < 0;

            if (Math.abs(this._lastDelta) >= threshold) {
                if (goingNext) {
                    this._snapToImage("right");
                } else {
                    this._snapToImage("left");
                }
            } else {
                this._snapToImage();
            }

            this._unbindTouches();
        },

        _unbindTouches() {
            this.releaseEvent("touchmove", SELECTOR_SLIDE_STRIP);
            this.releaseEvent("touchend", SELECTOR_SLIDE_STRIP);
        },

        /**
         * When you click an image, display a fullscreen gallery
         * @param event
         * @private
         */
        _onItemClick(event) {
            if (!this._preventDefaultExceptLink(event)) {
                // allow link clicks to pass through
                return;
            }

            event.stopPropagation();

            if (this.swiping) {
                return;
            }

            if (this.options.emitClickEvent) {
                var slideNode = traversal.closest(event.target, ".slide-control__slide");
                var index = slideNode ? slideNode.getAttribute("data-index") : this._currentIndex;
                index = parseInt(index);
                this.emit("slideClick", { index: index });
                return;
            }

            var clickPositionX = this._getClickPosition(event);

            if (event.currentTarget.offsetWidth / 3.3 > clickPositionX) {
                this._snapToImage("left");
            } else {
                this._snapToImage("right");
            }
        },

        /**
         * When you click an image, and not moved it, go to the next image.
         * @param event
         * @private
         */
        _getClickPosition(event) {
            return event.clientX - style.getPosition(event.currentTarget).left;
        },

        /**
         * Snaps to either the next or previous image, or just the current one.
         * @param {String} direction - 'left' or 'right' or none.
         * @private
         */
        _snapToImage(direction) {
            if (this._blocked) {
                return;
            }

            var translate;

            // now we need to work out how to snap
            var imageContainerWidth = this.el.querySelector(SELECTOR_SLIDE).clientWidth;

            if (direction === "left") {
                this._setSelectedIndex(this._getRelativeIndex(-1));
                translate = "translateX(" + imageContainerWidth + "px)";
            } else if (direction === "right") {
                this._setSelectedIndex(this._getRelativeIndex(1));
                translate = "translateX(" + -1 * imageContainerWidth + "px)";
            } else {
                translate = "translateX(0)";
            }

            var slideStrip = this.el.querySelector(SELECTOR_SLIDE_STRIP);
            slideStrip.classList.add(CLASS_SLIDE_STRIP_SNAPPING);

            requestAnimationFrame(() => {
                this.block();
                events.doOnce(
                    slideStrip,
                    browser.transitionEndEvent,
                    this._finishSnapAnimation.bind(this)
                );
                style.setStyle(slideStrip, "transform", translate);
            });
        },

        /**
         * Complete the snap animation, by fixing the left position and removing any
         * transforms.
         * @private
         */
        _finishSnapAnimation() {
            this.unblock();

            var slideStrip = this.el.querySelector(SELECTOR_SLIDE_STRIP);
            slideStrip.classList.remove(CLASS_SLIDE_STRIP_SNAPPING);
            style.setStyles(slideStrip, {
                transform: "",
                left: 0,
            });

            this._renderContent();
        },

        _renderContent() {
            var items = this._getItemsForIndex(this._currentIndex);

            // keep references of images we've already rendered, we don't want to reposition
            // them during a render pass
            var _renderedItems = [];

            _(this.el.querySelectorAll(SELECTOR_SLIDE)).each(function(element, elementIndex) {
                var content = items[elementIndex];
                var currentIndex = parseInt(element.getAttribute("data-index"));
                if (currentIndex === content.index) {
                    // don't do anything
                    return;
                }

                var outerNode = null;

                // we need to overwrite the old cached dom element with the new one to
                // make sure that we use the cached image
                var oldImage = element.querySelector(".slide-control__image");
                if (oldImage && !this._cachedImages[oldImage.src]) {
                    this._cachedImages[oldImage.src] = oldImage;
                }

                if (content.type === "image") {
                    var url = content.full || content.full_size_url || content.large_url;
                    var url2x = content.full2x || content.large_url_2x;

                    outerNode = document.createElement("a");
                    // this should link to the full sized image
                    outerNode.setAttribute("href", content.full_size_url || url);

                    var cachedImage = this._cachedImages[url];

                    if (cachedImage && _renderedItems.indexOf(cachedImage) < 0) {
                        outerNode.appendChild(cachedImage);
                        _renderedItems.push(cachedImage);
                    } else {
                        var imgNode = document.createElement("img");
                        imgNode.setAttribute("src", url);
                        this._setRetinaImageUrl(imgNode, url, url2x);
                        imgNode.setAttribute("data-zoomable", "true");
                        imgNode.classList.add("slide-control__image");
                        outerNode.appendChild(imgNode);

                        this._changeImageLoadingState(imgNode, element, outerNode);
                    }
                } else if (content.type === "html") {
                    outerNode = document.createElement("div");

                    if (_.isString(content.content)) {
                        outerNode.innerHTML = content.content;
                        element.classList.remove("slide-control__slide--is-loading");
                    } else if (_.isElement(content.content)) {
                        outerNode.appendChild(content.content);
                        element.classList.remove("slide-control__slide--is-loading");
                    } else {
                        console.error(
                            'If type is "html" then content needs to be either a DOM element or an HTML string.'
                        );
                    }
                } else {
                    console.error("No handler for slide type", content.type);
                }

                outerNode.classList.add("slide-control__slide__container");

                emptyElement(element);
                element.appendChild(outerNode);
                element.setAttribute("data-index", content.index);
            }, this);
        },

        _setRetinaImageUrl(imgNode, url, url2x) {
            if (url2x) {
                imgNode.setAttribute("srcset", `${url} 1x, ${url2x} 2x`);
            }
        },

        _changeImageLoadingState(imgNode, element, outerNode) {
            // if the image has no natural width, then it hasn't loaded yet
            if ("naturalWidth" in imgNode && !imgNode.naturalWidth) {
                element.classList.add("slide-control__slide--is-loading");
                let spinnerWrapper = document.createElement("div");
                spinnerWrapper.innerHTML = templateLoadingSpinner({
                    isWhite: this.options.whiteLoadingIndicator,
                });
                outerNode.appendChild(spinnerWrapper.firstChild);
            } else {
                // just make sure it's no longer loading anymore
                element.classList.remove("slide-control__slide--is-loading");
            }
        },

        /**
         * Remove loading square when loading completed.
         * @param e
         * @private
         */
        _onImageLoaded(e) {
            var target = e.target;
            if (!target) {
                return;
            }

            var slide = traversal.closest(target, ".slide-control__slide--is-loading");
            if (!slide) {
                return;
            }

            var existingImage = slide.querySelector(".slide-control__image");
            if (existingImage !== e.target) {
                return;
            }

            slide.classList.remove("slide-control__slide--is-loading");
            var loadingSquare = slide.querySelector(".loading-square");
            if (loadingSquare) {
                loadingSquare.parentNode.removeChild(loadingSquare);
            }
        },

        /**
         * Show an error message when the image has failed to load.
         * @param event
         * @private
         */
        _onImageFailed(event) {
            var target = event.target;
            if (!target) {
                return;
            }

            target.style.display = "none";
            var errorMessage = document.createElement("div");
            errorMessage.classList.add("slide-control__image__error");
            errorMessage.textContent = "There was a problem loading this image.";
            target.parentElement.appendChild(errorMessage);

            var loadingSquare = target.parentElement.querySelector(".loading-square");
            if (loadingSquare) {
                loadingSquare.parentElement.removeChild(loadingSquare);
            }
        },

        /**
         * Updates the selected index, the thumbnails and the indicator
         * @param index - the index to go to
         * @param fromIndicator - if this action was triggered by an indicator
         * @private
         */
        _setSelectedIndex(index, fromIndicator) {
            var old = this._currentIndex;
            this._currentIndex = index;
            if (old !== index) {
                this.emit("currentSlideChange", {
                    index: index,
                    oldIndex: old,
                    fromIndicator: fromIndicator,
                });
            }
        },

        /**
         * Gets the index relative to another index. For example, if you specify -1, it will be
         * the previous item, and 1 to get the next item.
         * @param relativePosition - the position to get
         * @param relativeTo - the index to check from, if null, takes the current index
         * @returns {*}
         * @private
         */
        _getRelativeIndex(relativePosition, relativeTo) {
            // if no index giving use the current selected one
            if (_.isUndefined(relativeTo)) {
                relativeTo = this._currentIndex;
            }

            if (relativePosition === 0) {
                return relativeTo;
            }

            var newIndex = relativeTo + relativePosition;

            // when first item is reached take the last items
            if (newIndex < 0) {
                newIndex = this.getMaxIndex() + (newIndex + 1);
            }

            if (newIndex > this.getMaxIndex()) {
                newIndex = newIndex - this.getMaxIndex() - 1;
            }
            if (_.isNull(this._currentIndex)) {
                this._setSelectedIndex(0);
            }

            return newIndex;
        },

        /**
         * Takes a index and returns an array with the previous and the next index
         * @param {number} index
         * @return {Array}
         * @private
         */
        _getItemsForIndex(index) {
            var displayed = [];
            var totalDisplayed = this.getRenderContext().totalDisplayedSlides;
            for (var i = -1; i < totalDisplayed - 1; i++) {
                displayed.push(i);
            }

            return displayed
                .map(_.partial(this._getRelativeIndex, _, index).bind(this))
                .map((thisIndex) => {
                    var item = this._items[thisIndex];
                    if (!item) {
                        item = this._items[0] || {};
                        thisIndex = 0;
                    }
                    item.index = thisIndex;
                    return item;
                });
        },
    }
);
